import { Paper, styled } from "@mui/material";
import { autorun } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import FocusManager from "../core/FocusManager";
import Localization from "../core/Localization";
import Sys from "../core/Sys";
import DocumentDropArea from "../coreui/table/DocumentDropArea";
import { ErrorMessage } from "../coreui/ErrorMessage";
import FocusTracker from "../coreui/FocusTracker";
import KeyboardNavigationGroup, {
  KeyboardNavigationGroup as KeyboardNavigationGroupBase,
} from "../coreui/KeyboardNavigationGroup";
import {
  GroupedNavigation,
  GroupedNavigationRef,
} from "../coreui/GroupedNavigation";
import Presentation from "../coreui/Presentation";
import { TableChildProps } from "../coreui/Table";
import { NoPrint } from "../coreui/table/NoPrint";
import {
  FilterProvider,
  getFilterWords,
  rowMatchesFilter,
} from "../coreui/table/FilterContext";
import { PaginationProvider } from "../coreui/table/PaginationContext";
import { TableProvider } from "../coreui/table/TableContext";
import Typography from "../coreui/Typography";
import PaneRow from "../models/PaneRow";
import { FileInfo } from "../stores/DocumentStore";
import PaneDataStore from "../stores/PaneDataStore";
import { Card } from "./Card";
import GridColumn, { GridColumnConfigProperties } from "./Columns/GridColumn";
import { ResponsiveGridContext } from "./ResponsiveGridContext";
import { Toolbar as ToolbarBase } from "./Toolbar";
import ErrorsStore from "../stores/ErrorsStore";

interface Props {
  cardConfig: object;
  cardDepth: number;
  columns: GridColumnConfigProperties[];
  contentDataId: string;
  description: string;
  documentUpload?: {
    files: FileInfo[];
    onUploadFiles: (files: FileList) => Promise<void>;
  };
  errors?: string[];
  footerToolbarConfig?: object;
  getAccessibleDescription: (rowCount: number) => string;
  headerToolbarConfig?: object;
  noDataMessage?: string;
  propagated: TableChildProps;
  resetPageOnPopulate?: true;
  selectionToolbarConfig?: object;
  setPopulate?: (populate: () => void) => void;
  setRestoreLostFocus?: (restoreLostFocus: () => void) => void;
  setScrollToRow?: (scrollToRow: ((rowKey: string) => void) | null) => void;
  summaryToolbarConfig?: object;
  tableKey: string;
}

const ErrorSummary = styled(Typography)(({ theme }) => ({
  color: theme.palette.danger.main,
  display: "grid",
  rowGap: "4px",
}));

const CardContainer = styled(GroupedNavigation)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
  gap: theme.spacing(3),
}));

const NoDataMessage = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "cardDepth",
})<{ cardDepth: number }>(({ cardDepth, theme }) => ({
  backgroundColor:
    cardDepth === 0 ? theme.palette.grey[100] : theme.palette.common.white,
  fontSize: "14px",
  lineHeight: "50px",
  paddingLeft: theme.spacing(2),
}));

const ToolbarFocusWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "focused",
})<{ focused: boolean }>(({ focused = false, theme }) => ({
  borderBottomColor: focused
    ? `${theme.palette.grey[800]} !important`
    : undefined,
  borderBottomWidth: focused ? "2px !important" : undefined,
  borderBottomStyle: focused ? ("solid" as const) : undefined,
  borderTopColor: focused ? `${theme.palette.grey[800]} !important` : undefined,
  borderTopWidth: focused ? "2px !important" : undefined,
  borderTopStyle: focused ? ("solid" as const) : undefined,
}));

export const CardControl = observer((props: Props): JSX.Element => {
  const responsiveGrid = React.useContext(ResponsiveGridContext);
  const [currentPageIndex, setCurrentPageIndex] = React.useState(0);
  const [filterValue, setFilterValue] = React.useState<string | null>(null);
  const [isFooterToolbarFocused, setIsFooterToolbarFocused] =
    React.useState(false);
  const [isHeaderToolbarFocused, setIsHeaderToolbarFocused] =
    React.useState(false);
  const [pageSize, setPageSize] = React.useState<number | null>(null);
  const [visibleRows, setVisibleRows] = React.useState<PaneRow[]>([]);

  const containerRef = React.useRef<GroupedNavigationRef>(null);
  const filterChanged = React.useRef(false);
  const focusOnMount = React.useRef(false);
  const headerToolbarRef = React.useRef<HTMLDivElement>(null);
  const lastUploadFilesCount = React.useRef(0);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const viewRows = React.useRef<PaneRow[]>([]);

  const populate = () => {
    if (props.resetPageOnPopulate) {
      setCurrentPageIndex(0);
    }
  };

  const restoreLostFocus = () => {
    containerRef.current?.restoreLostFocus();
  };

  const scrollToRow = (rowKey: string) => {
    const cardId = `${props.tableKey}-${rowKey}`;
    const cardElement = document.getElementById(cardId);
    cardElement?.scrollIntoView({ behavior: "instant", block: "start" });
  };

  // FUTURE
  // React Context solves the same problem that "propagated" is trying to,
  // but Context allows a scoped "publish subscribe" model that doesn't so
  // tightly couple the components. Ideally, we would move all of "propagated"
  // to use Context, but are not doing so right now to contain scope.
  const propagated: TableChildProps = {
    ...props.propagated,
    parentTable: {
      ...props.propagated.parentTable,
      isVerticalLayout: true,
      focusHeaderToolbar: () => {
        if (headerToolbarRef.current) {
          const focusable = KeyboardNavigationGroupBase.getFocusableChildren(
            headerToolbarRef.current,
            ToolbarBase.childSelector
          );
          if (focusable.length > 0) {
            (focusable[0] as HTMLElement).focus();
          }
        }
      },
      focusNewRow: () => {
        const newRow = viewRows.current[0]; // The new row is always first.
        const cardId = `${props.tableKey}-${newRow.rowKey}`;
        const newRowCard = document.getElementById(cardId);
        newRowCard?.focus();
      },
      goToFirstPage: () => setCurrentPageIndex(0),
      scrollToRow: scrollToRow,
    },
  };

  const rows: PaneRow[] = PaneDataStore.getPaneCollection(props.contentDataId);

  React.useEffect(() => {
    focusOnMount.current = responsiveGrid.hasFocus;
  }, []);

  // Provide a populate function to the parent component if requested
  React.useEffect(() => {
    props.setPopulate?.(populate);
  }, [props.setPopulate]);

  // Provide a restoreLostFocus function to the parent component if requested
  React.useEffect(() => {
    props.setRestoreLostFocus?.(restoreLostFocus);
  }, [props.setRestoreLostFocus]);

  // Provide a scrollToRow function to the parent component if requested
  React.useEffect(() => {
    props.setScrollToRow?.(scrollToRow);
  }, [props.setScrollToRow]);

  // Detect newly uploaded files and scroll to the newest one
  React.useEffect(() => {
    if (!props.documentUpload?.files) {
      return;
    }
    const uploadFiles: FileInfo[] = props.documentUpload.files;
    return autorun(() => {
      const finalizedFiles = uploadFiles.filter(
        (f) => f.status === "Finalized"
      );
      if (finalizedFiles.length > lastUploadFilesCount.current) {
        const newestFile: FileInfo = finalizedFiles[finalizedFiles.length - 1];
        scrollToRow(newestFile.file["rowKey"]);
      }
      lastUploadFilesCount.current = finalizedFiles.length;
    });
  }, [props.documentUpload?.files]);

  // Determine visible rows based on filter value
  // Note that a side-effect of storing visibleRows in state is that the row
  // rendering is deferred by a single render cycle. This is actually desirable
  // behaviour because it allows the pagination to be initialized before we ever
  // attempt to render any rows. If the pagination is not initialized on time,
  // all rows will be rendered (since the default page size of null is used),
  // which may result in a significant lag spike on initial render even though
  // visually it will appear that pagination is successfully applied (since the
  // subsequent render that applies the page size will be comparatively fast).
  React.useEffect(
    () =>
      autorun(() => {
        let newVisibleRows: PaneRow[] = [];
        if (filterValue !== null) {
          const filterWords = getFilterWords(filterValue);
          for (const row of rows) {
            if (rowMatchesFilter(row, filterWords, props.columns, propagated)) {
              newVisibleRows.push(row);
            }
          }
        } else {
          newVisibleRows = rows;
        }
        setVisibleRows(newVisibleRows);
      }),
    [filterValue]
  );

  // Announce the number of visible rows after filter change
  React.useEffect(() => {
    if (!filterChanged.current) {
      // Visible rows didn't change due to a filter change.
      return;
    }
    filterChanged.current = false;
    const count = visibleRows.length;

    let messageCode: string;
    if (count === 0) {
      messageCode = "DataTable.gridFilterAlertZero";
    } else if (count === 1) {
      messageCode = "DataTable.gridFilterAlertSingle";
    } else {
      messageCode = "DataTable.gridFilterAlertMultiple";
    }
    const message = Localization.getBuiltInMessage(messageCode, { count });

    Sys.debounceMethod(
      () => Sys.announce(message),
      "DataTable.gridFilterAlert",
      1000
    );
  }, [visibleRows]);

  React.useEffect(() => {
    if (focusOnMount.current) {
      // Delay by one frame to ensure the render fires first
      window.setTimeout(() => {
        focusOnMount.current = false;
        FocusManager.grabFocusForChild(rootRef.current!, "[data-card]");
      });
    }
  }, [visibleRows]);

  // Use pagination to determine which rows to display
  const totalRows = visibleRows.length;
  let firstRowIndex = 0;
  let lastRowIndex = totalRows - 1;
  if (pageSize !== null) {
    firstRowIndex = currentPageIndex * pageSize;
    if (firstRowIndex >= totalRows && currentPageIndex > 0) {
      firstRowIndex = 0;
      setCurrentPageIndex(0);
    }
    lastRowIndex = Math.min(firstRowIndex + pageSize - 1, totalRows - 1);
  }

  viewRows.current = visibleRows.slice(firstRowIndex, lastRowIndex + 1);

  const accessibleDescriptionId = `${props.tableKey}-accessible-description`;
  let describedBy: string = accessibleDescriptionId;

  const errorSummaryId = `${props.tableKey}-error-summary`;
  // Get the row errors so they can be provided to the Cards as they are rendered.
  const rowErrors = ErrorsStore.getTableRowErrors(props.contentDataId, (w) =>
    GridColumn.isColumnVisible(props.columns, w.widgetName, "xs")
  );
  const errorCount: number = ErrorsStore.getTableErrorCount(
    props.contentDataId
  );

  const hasErrors =
    props.errors !== undefined && (errorCount > 0 || props.errors.length > 0);
  if (hasErrors) {
    // Ensure errors are announced by VoiceOver.
    describedBy += ` ${errorSummaryId}`;
  }

  return (
    <div ref={rootRef}>
      <TableProvider visibleRows={visibleRows}>
        <FilterProvider
          filterValue={filterValue}
          onFilterChange={(filterValue) => {
            filterChanged.current = true;
            setFilterValue(filterValue);
          }}
        >
          {hasErrors ? (
            <Paper elevation={2} id={errorSummaryId} sx={{ padding: 2 }}>
              <ErrorSummary component="div">
                {props.errors?.map((error) => (
                  <ErrorMessage key={error} message={error} />
                ))}
                {errorCount > 0 ? (
                  <ErrorMessage
                    key="table-error-summary"
                    message={`${errorCount} ${
                      errorCount === 1
                        ? Localization.getBuiltInMessage("errorTable")
                        : Localization.getBuiltInMessage("errorsTable")
                    }`}
                  />
                ) : null}
              </ErrorSummary>
            </Paper>
          ) : null}
          {props.headerToolbarConfig && (
            <FocusTracker onFocusChanged={setIsHeaderToolbarFocused}>
              <KeyboardNavigationGroup
                childSelector={ToolbarBase.childSelector}
                role="toolbar"
                aria-label={Localization.getBuiltInMessage(
                  "DataTable.toolbarLabel",
                  {
                    description: propagated.parentTable.description,
                  }
                )}
              >
                <ToolbarFocusWrapper focused={isHeaderToolbarFocused}>
                  <NoPrint ref={headerToolbarRef}>
                    {Presentation.create(props.headerToolbarConfig, propagated)}
                  </NoPrint>
                </ToolbarFocusWrapper>
              </KeyboardNavigationGroup>
            </FocusTracker>
          )}
          {props.documentUpload && (
            <DocumentDropArea
              dataId={props.contentDataId}
              files={props.documentUpload.files}
              renderProgressDialog={true}
              uploadFiles={props.documentUpload.onUploadFiles}
            />
          )}
          <CardContainer
            aria-describedby={describedBy}
            aria-errormessage={hasErrors ? errorSummaryId : undefined}
            aria-invalid={hasErrors}
            aria-label={props.description}
            firstRowIndex={firstRowIndex}
            onBlur={(event: React.FocusEvent<HTMLDivElement>) => {
              const focusIsOutside = !event.currentTarget.contains(
                event.relatedTarget as Node
              );
              const focusWasInside =
                event.target instanceof HTMLElement &&
                event.currentTarget.contains(event.target as Node);
              const focusIsLeaving = focusWasInside && focusIsOutside;

              if (focusIsLeaving) {
                responsiveGrid.setHasFocus(false);
              }
            }}
            onFocus={(event: React.FocusEvent<HTMLDivElement>) => {
              const focusWasOutside = !event.currentTarget.contains(
                event.relatedTarget as Node
              );
              const focusIsInside = event.currentTarget.contains(
                event.target as Node
              );
              const focusIsEntering = focusWasOutside && focusIsInside;

              if (focusIsEntering) {
                responsiveGrid.setHasFocus(true);
              }
            }}
            ref={containerRef}
            totalRows={totalRows}
          >
            {viewRows.current.map((row, index) => (
              <Card
                cardIndex={index}
                cardDepth={props.cardDepth}
                data-card
                errors={
                  // Passing a value to errors is the way for this component
                  // to opt-in to error display.
                  props.errors !== undefined
                    ? rowErrors.get(row.rowKey)
                    : undefined
                }
                key={row.rowKey}
                id={`${props.tableKey}-${row.rowKey}`}
                paneConfig={props.cardConfig}
                propagated={propagated}
                row={row}
                selectionToolbarConfig={props.selectionToolbarConfig}
              />
            ))}
          </CardContainer>
          {totalRows === 0 && props.noDataMessage && (
            <NoDataMessage cardDepth={props.cardDepth}>
              {props.noDataMessage}
            </NoDataMessage>
          )}
          {props.summaryToolbarConfig && (
            <div
              style={{
                marginBottom: props.footerToolbarConfig ? 24 : 0,
                marginTop: 16,
              }}
            >
              {Presentation.create(props.summaryToolbarConfig, propagated)}
            </div>
          )}
          {props.footerToolbarConfig && (
            <PaginationProvider
              currentPageIndex={currentPageIndex}
              firstRowIndex={firstRowIndex}
              lastRowIndex={lastRowIndex}
              onGoToNextPage={() => setCurrentPageIndex((prev) => prev + 1)}
              onGoToPreviousPage={() => setCurrentPageIndex((prev) => prev - 1)}
              onInitialize={() => setPageSize(1)}
              onSetPageSize={(newPageSize: number | null) => {
                setPageSize(newPageSize);
                setCurrentPageIndex(0);
              }}
              pageSize={pageSize}
              pageSizes={[1, 5, 10, 20, null]}
              scrollTargetRef={rootRef}
              totalPages={Math.ceil(totalRows / (pageSize || totalRows))}
              totalRows={totalRows}
            >
              <FocusTracker onFocusChanged={setIsFooterToolbarFocused}>
                <KeyboardNavigationGroup
                  childSelector={ToolbarBase.childSelector}
                  role="toolbar"
                  aria-label={Localization.getBuiltInMessage(
                    "DataTable.footerToolbarLabel",
                    {
                      description: propagated.parentTable.description,
                    }
                  )}
                >
                  <ToolbarFocusWrapper focused={isFooterToolbarFocused}>
                    <NoPrint>
                      {Presentation.create(
                        props.footerToolbarConfig,
                        propagated
                      )}
                    </NoPrint>
                  </ToolbarFocusWrapper>
                </KeyboardNavigationGroup>
              </FocusTracker>
            </PaginationProvider>
          )}
          {props.getAccessibleDescription && (
            <div id={accessibleDescriptionId} style={{ display: "none" }}>
              {props.getAccessibleDescription(visibleRows.length)}
            </div>
          )}
        </FilterProvider>
      </TableProvider>
    </div>
  );
});
